import store from "../redux/store";
import { handleJoiSchemaError } from "../utils";
import { openMessageModal } from "../utils/common";
import { logHighError, logMediumError } from "../utils/logger";
import {
  API_CUSTOMER_TRANSFER,
  API_DELETE_USER_ORDER,
  API_GET_USER,
  API_PUT_USERS_SHOP_DATA,
  API_USER_PROMO_CODE,
  API_DECISION_2FA_API,
  API_VERIFY_2FA_API,
  API_USER_LOYALTY_POINTS
} from "./api";
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPostRequest,
  sendPutRequest,
} from "./backend";

export const updateUserShopData = async (
  userId: string,
  shopId: string,
  shopData: any,
): Promise<boolean> => {
  try {
    const { companyId } = store.getState().configurationReducer;
    await sendPutRequest(
      API_PUT_USERS_SHOP_DATA(companyId, userId, shopId),
      shopData,
    );

    return true;
  } catch (error) {
    logMediumError(
      `Error setting user shop ids ${handleJoiSchemaError(error)}`,
      { userId, shopId, shopData },
    );
    return false;
  }
};

export const deleteUserLastOrderChargeId = async (
  userId: string,
  orderId: string,
) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    await sendDeleteRequest(API_DELETE_USER_ORDER(companyId, userId, orderId));
  } catch (error) {
    logMediumError(
      `Could not delete user (${userId}) order (${orderId}) for company (${companyId}) ${handleJoiSchemaError(
        error,
      )}`,
      { userId, orderId },
    );
    throw error;
  }
};

export const getUser = async (userId: string): Promise<any> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const response = await sendGetRequest(
      API_GET_USER(companyId, userId),
      null,
      false,
    );

    return response.data;
  } catch (error) {
    logMediumError(
      `Error getting user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error,
      )}`,
      { userId },
    );
    throw error;
  }
};

export const deleteUserPromotion = async (
  userId: string,
  promoCodeKey: string,
): Promise<boolean> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    await sendDeleteRequest(
      API_USER_PROMO_CODE(companyId, userId, promoCodeKey),
    );

    return true;
  } catch (error) {
    logMediumError(
      `Error deleting user (${userId}) promotion (${promoCodeKey}) for company (${companyId}) ${handleJoiSchemaError(
        error,
      )}`,
      { userId, promoCodeKey },
    );
    throw error;
  }
};

export const transferCustomerData = async (
  originUserId: string,
  destinationUserId: string,
  triggeredByLogin: boolean,
): Promise<boolean> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const { data = null } = await sendPostRequest(
      API_CUSTOMER_TRANSFER(companyId),
      { originUserId, destinationUserId, triggeredByLogin },
      false,
    );
    if (data?.message) {
      openMessageModal(data?.message);
    }
    return true;
  } catch (error) {
    logHighError(
      `Error transfering data from user (${originUserId}) to user (${destinationUserId}) for company (${companyId}) ${handleJoiSchemaError(
        error,
      )}`,
      { originUserId, destinationUserId, triggeredByLogin, companyId },
    );
    return false;
  }
};
export const decision2FA = async (email: string) => {
  try {
    const { companyId } = store.getState().configurationReducer;

    const response = await sendPostRequest(
      API_DECISION_2FA_API(companyId),
      {
        email,
      },
      false,
    );

    return response.data;
  } catch (error: any) {
    throw error?.errorMessage ?? error;
  }
};

export const verify2FA = async (email: string, code: string) => {
  try {
    const { companyId } = store.getState().configurationReducer;

    const data = {
      email,
      code,
    };

    const response = await sendPostRequest(
      API_VERIFY_2FA_API(companyId),
      data,
      false,
    );

    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getUserLoyaltyPoints = async (companyId: string, userId: string): Promise<number> => {
  try {
    if (userId) {
      const url = API_USER_LOYALTY_POINTS(companyId, userId);
      const response = await sendGetRequest(url, null, false);
      return response.data?.points ?? 0;
    }
    return 0;
  } catch (error) {
    return 0;
  }
};